'use client';

import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, CheckCircle, AlertCircle } from "lucide-react";
import { useSubmitContactMessage } from "@/server/contact";

interface FormData {
  name: string;
  email: string;
  mobile: string;
  part: string;
  description: string;
  status: string;
}

const parts = [
  "مشاوره خرید محصول",
  "پشتیبانی فنی",
  "همکاری تجاری",
  "شکایت و انتقاد",
  "سایر موارد"
];

export default function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    mobile: '',
    part: '',
    description: '',
    status: 'pending'
  });
  
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Partial<FormData>>({});
  
  const { mutate: submitMessage, isPending: isSubmitting } = useSubmitContactMessage();

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'نام و نام خانوادگی الزامی است';
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'فرمت ایمیل صحیح نیست';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'شماره تماس الزامی است';
    } else if (!/^[\d\s\-\+\(\)]+$/.test(formData.mobile)) {
      newErrors.mobile = 'فرمت شماره تماس صحیح نیست';
    }

    if (!formData.part) {
      newErrors.part = 'موضوع پیام الزامی است';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'متن پیام الزامی است';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'متن پیام باید حداقل ۱۰ کاراکتر باشد';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitStatus('idle');

    submitMessage(formData, {
      onSuccess: () => {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          mobile: '',
          part: '',
          description: '',
          status:''
        });
      },
      onError: (error) => {
        console.error('Form submission error:', error);
        setSubmitStatus('error');
      }
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Success/Error Messages */}
      {submitStatus === 'success' && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <div>
            <h4 className="font-medium text-green-800">پیام شما با موفقیت ارسال شد!</h4>
            <p className="text-sm text-green-600">در اسرع وقت با شما تماس خواهیم گرفت.</p>
          </div>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <div>
            <h4 className="font-medium text-red-800">خطا در ارسال پیام</h4>
            <p className="text-sm text-red-600">لطفاً دوباره تلاش کنید یا با ما تماس بگیرید.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-medium text-gray-700">
            نام و نام خانوادگی *
          </Label>
          <Input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={`${errors.name ? 'border-red-300 focus:border-red-500' : ''}`}
            placeholder="نام کامل خود را وارد کنید"
          />
          {errors.name && (
            <p className="text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-gray-700">
            ایمیل (اختیاری)
          </Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`${errors.email ? 'border-red-300 focus:border-red-500' : ''}`}
            placeholder="example@domain.com"
          />
          {errors.email && (
            <p className="text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="mobile" className="text-sm font-medium text-gray-700">
            شماره تماس *
          </Label>
          <Input
            id="mobile"
            type="tel"
            value={formData.mobile}
            onChange={(e) => handleInputChange('mobile', e.target.value)}
            className={`${errors.mobile ? 'border-red-300 focus:border-red-500' : ''}`}
            placeholder="۰۹۱۲۳۴۵۶۷۸۹"
          />
          {errors.mobile && (
            <p className="text-sm text-red-600">{errors.mobile}</p>
          )}
        </div>


        {/* Subject */}
        <div className="space-y-2">
          <Label htmlFor="part" className="text-sm font-medium text-gray-700">
            موضوع پیام *
          </Label>
          <Select value={formData.part} onValueChange={(value) => handleInputChange('part', value)}>
            <SelectTrigger className={`${errors.part ? 'border-red-300 focus:border-red-500' : ''}`}>
              <SelectValue placeholder="موضوع پیام خود را انتخاب کنید" />
            </SelectTrigger>
            <SelectContent>
              {parts.map((part) => (
                  <SelectItem key={part} value={part}>
                    {part}
                  </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.part && (
              <p className="text-sm text-red-600">{errors.part}</p>
          )}
        </div>

      </div>


      {/* Message */}
      <div className="space-y-2">
        <Label htmlFor="description" className="text-sm font-medium text-gray-700">
          متن پیام *
        </Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          className={`min-h-[120px] ${errors.description ? 'border-red-300 focus:border-red-500' : ''}`}
          placeholder="پیام خود را به تفصیل بنویسید..."
        />
        {errors.description && (
          <p className="text-sm text-red-600">{errors.description}</p>
        )}
        <p className="text-xs text-gray-500">
          حداقل ۱۰ کاراکتر - {formData.description.length} کاراکتر نوشته شده
        </p>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={isSubmitting}
          className="bg-gradient-to-r from-[#252360] to-[#4451A3] hover:from-[#4451A3] hover:to-[#252360] text-white font-semibold px-8 py-3 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              در حال ارسال...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              ارسال پیام
            </>
          )}
        </Button>
      </div>

      {/* Privacy Notice */}
      <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
        <p>
          🔒 اطلاعات شما محفوظ است و تنها برای پاسخگویی به درخواست شما استفاده می‌شود.
          ما هرگز اطلاعات شخصی شما را با اشخاص ثالث به اشتراک نمی‌گذاریم.
        </p>
      </div>
    </form>
  );
}
